import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  useMediaQuery,
    Checkbox
} from '@mui/material';
import TickerChip from "../../components/TickerChip";
import { DoubleLine } from "../../components/Labels";
import { addToSelectedList, removeFromSelectedList, selectSelectedList } from '../../store/slices/tickers.slice';

const TickerTable = ({ tickers = [], selectable=false }) => {
  const dispatch = useDispatch();

  const isMobile = useMediaQuery('(max-width: 768px)');
  const selectedStocks = useSelector(state => selectSelectedList(state, 'stockSelectedList'));
  const selectedEtfs = useSelector(state => selectSelectedList(state, 'etfSelectedList'));


  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const isSymbolSelected = (symbol, type) => {
    return type === 'etf' ?
      selectedEtfs.includes(symbol) :
      selectedStocks.includes(symbol);
  };

  const getListFieldForType = (type) => {
    return type === 'etf' ? 'etfSelectedList' : 'stockSelectedList';
  };

  const handleSelectAll = (event) => {
    const visibleTickers = tickers
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      // Group tickers by type
      .reduce((acc, ticker) => {
        const listField = getListFieldForType(ticker.type);
        if (!acc[listField]) acc[listField] = [];
        acc[listField].push(ticker.symbol);
        return acc;
      }, {});

    // Handle each type separately
    Object.entries(visibleTickers).forEach(([listField, symbols]) => {
      if (event.target.checked) {
        dispatch(addToSelectedList({ listField, symbols }));
      } else {
        dispatch(removeFromSelectedList({ listField, symbols }));
      }
    });
  };

  const handleSelectOne = (ticker, checked) => {
    const listField = getListFieldForType(ticker.type);
    if (checked) {
      dispatch(addToSelectedList({ listField, symbols: ticker.symbol }));
    } else {
      dispatch(removeFromSelectedList({ listField, symbols: ticker.symbol }));
    }
  };

  const formatPercentage = (value, multiplier = 100) => {
    if (value == null) return '-';
    return `${(value * multiplier).toFixed(2)}%`;
  };

  const columns = [
    ...(selectable ? [{
      id: 'selection',
      minWidth: 50,
      render: (row) => (
        <Checkbox
          checked={isSymbolSelected(row.symbol, row.type)}
          onChange={(e) => handleSelectOne(row, e.target.checked)}
        />
      )
    }] : []),
    {
      id: 'security',
      label: 'Security',
      minWidth: 170,
      render: (row) => <TickerChip ticker={row} />
    },
    {
      id: 'category',
      label: 'Category',
      minWidth: 130,
      render: (row) => (
        <DoubleLine
          title={row.tags.sector}
          text={row.tags.industry}
        />
      )
    },
    {
      id: 'divYield',
      label: 'Div Yield',
      minWidth: 100,
      render: (row) => formatPercentage(row.metrics?.dy)
    },
    {
      id: 'exchange',
      label: 'Exchange',
      minWidth: 100,
      render: (row) => row.exchange?.toUpperCase()
    }
  ];

  const desktopColumns = [
    ...columns,
    {
      id: 'correlation',
      label: 'Corr (S&P 1Yr)',
      minWidth: 120,
      render: (row) => formatPercentage(row.stats?.benchmarks?.spy?.corr_1_yr)
    },
    {
      id: 'upside',
      label: 'Upside',
      minWidth: 100,
      render: (row) => formatPercentage(row.stats?.benchmarks?.spy?.upside, 1)
    },
    {
      id: 'downside',
      label: 'Downside',
      minWidth: 100,
      render: (row) => formatPercentage(row.stats?.benchmarks?.spy?.downside, 1)
    }
  ];

  const activeColumns = isMobile ? columns : desktopColumns;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const commonStyle = {
    backgroundColor: '#fff',
    fontWeight: 600,
    zIndex: 3,
    position: 'sticky',
    top: 0,
    borderBottom: '2px solid #e0e0e0',
  }

  return (
    <Paper
      elevation={0}
      square
      sx={{
        width: '100%',
        overflow: 'hidden',
        backgroundColor: 'transparent',
        position: 'relative',
        height: 'calc(100vh - 200px)' // Adjust based on your header/padding
      }}
    >
      <TableContainer
        sx={{
          maxHeight: '100%',
          borderRadius: 0,
          '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: 0,
          }
        }}
      >
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {selectable && (
                 <TableCell sx={{...commonStyle}}>
                  <Checkbox
                    onChange={handleSelectAll}
                    size={'small'}
                    sx={{
                      p: 0,
                      bgcolor: 'transparent',
                      '&:hover': { bgcolor: 'transparent' }
                    }}
                    checked={
                      tickers
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .every(ticker => isSymbolSelected(ticker.symbol, ticker.type))
                    }
                    indeterminate={
                      tickers
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .some(ticker => isSymbolSelected(ticker.symbol, ticker.type)) &&
                      !tickers
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .every(ticker => isSymbolSelected(ticker.symbol, ticker.type))
                    }
                  />
                </TableCell>
              )}
              {activeColumns.map((column) => (

                <TableCell
                  key={column.id}
                  sx={{
                    ...commonStyle,
                    minWidth: column.minWidth,
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      height: '1px',
                      bottom: '-1px',
                      backgroundColor: '#e0e0e0'
                    }
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tickers
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={row.id || index}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 }
                  }}
                >
                  {activeColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      sx={{
                        backgroundColor: '#fff'
                      }}
                    >
                      {column.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={tickers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#fff',
          position: 'sticky',
          bottom: 0,
          zIndex: 2
        }}
      />
    </Paper>
  );
};

export default TickerTable;